import Application from './Application';
import common from './common';
import PreloaderScene from './scenes/PreloaderScene';
import MultiStyleText from './pixi-multistyle-text';

export default class Main {
	/**
     * @param {number} [width=1024]
     * @param {number} [height=768]
     */
	constructor(width = 1024, height = 768) {
		this._width = width;
		this._height = height;
		this._app = null;
		this._preloader = null;
		this._themes = ['winter', 'christmas', 'spring', 'summer', 'autumn', 'halloween'];
	}

	init() {
		P3.assets.audioFileTypes = ['ogg', 'mp3'];

		PIXI.settings.RETINA_PREFIX = /\_(?=[^_]*$)(.+)x/;
		PIXI.settings.MIPMAP_TEXTURES = false;
		P3.settings.WARN_TEXTURE_DIVISIBLE_BY_2 = false;

		typeof TweenMax !== 'undefined' && (TweenMax.defaultOverwrite = 'none');

		const options = {
			width: this._width,
			height: this._height,
			holderId: 'game',
			portrait: false,
			rotateImageUrl: '',
			rotateImageColor: '#000000',
			aspectRatioFillHeight: true
		};

		const view = new P3.View(options);
		view.signals.ready.once(function(canvas) {
			const options = {};
			options.view = canvas;
			options.transparent = false;
			options.antialias = false;
			options.preserveDrawingBuffer = false;
			options.resolution = 1;

			const stage = new PIXI.Container();
			common.stage = stage;

			P3.input.init(stage, canvas);

			if (P3.utils.isCocoonJS()) {
				stage.scale.x = window.innerHeight / this._height;
				stage.scale.y = window.innerHeight / this._height;
			}

			const renderer = !P3.utils.isCocoonJS()
				? PIXI.autoDetectRenderer(this._width, this._height, options)
				: new PIXI.WebGLRenderer(window.innerWidth, window.innerHeight, options);
			renderer.backgroundColor = 0xFFFFFF;
			common.renderer = renderer;

			common.stage.addChild(P3.scene.view);

			const transitions = new PIXI.Container();
			common.stage.addChild(transitions);
			common.transitions = transitions;

			const ticker = new P3.Ticker();
			ticker.init(function() {
				P3.scene.update();
				P3.input.update();
				P3.utils.TWEEN.update(); // comment this line out if you are using TweenMax
			}, function() {
				renderer.render(stage);
			}, this);
			common.ticker = ticker;

			this.loadPreAssets();
		}, this);
		view.signals.resize.add(this.onCanvasResize, this);
		common.view = view;

		this.loadConfig(function() {view.init();}, this); // we load the config before we do anything
	}

	loadConfig(callback, scope) {
		scope = scope || window;
		P3.assets.addFiles([{name: 'config', url: 'config' + '.json'}], './');
		P3.assets.signals.complete.once(function() {
			common.config = P3.assets.json('config');
			common.config.language = window.__language;
			callback && callback.call(scope);
		}, this);
		P3.assets.load();
	}

	loadPreAssets() {
		const files = [
			{name: 'bg_preloader', url: 'images/preloader/' + 'bg_preloader_pot' + '.jpg'},
			{name: 'preloader_1x', url: 'images/preloader/' + 'preloader_1x' + '.json'},
			{name: 'game_config', url: 'data/' + 'game_config' + '.json'},
			{name: 'level_config', url: 'data/themes/' + `level_data_${common.config.theme}` + '.json'},
			{name: 'ui_0_1x', url: `images/${common.config.theme}/ui/` + 'ui_0_1x' + '.json'},
			{name: 'ui_1_1x', url: `images/${common.config.theme}/ui/` + 'ui_1_1x' + '.json'}
		];
		if (files.length) {
			P3.assets.addFiles(files, './assets/');
			P3.assets.signals.complete.once(()=> {
				/**
				 * Get theme from url param... ?theme=
				 */
				let getParameter = (param, validThemes, sliceNum)=> {
					let para = param[0].slice(sliceNum);
					if (validThemes.includes(para.toString()))
						return para;
					else
						throw new Error(common.config.debug_comment);
				};

				if (window.location.href) {
					let m = window.location.href.match(/theme\=([a-z0-9\_\-]+)/gi);
					if (m)
						common.config.theme = getParameter(m, this._themes, 6);
				}

				//update the rotate texture
				common.view._rotateImage.style.backgroundImage = 'url(' + `./assets/images/_localisation/rotate/${common.config.theme}/${common.config.language}_rotate.jpg` + ')';
				common.gameConfig = P3.assets.json('game_config');
				common.levelConfig = P3.assets.json('level_config');
				common.gameConfig.palettePanel.pages[common.gameConfig.palettePanel.tools.stamp[0]] = common.levelConfig.stamps;
				this.loadAssets();
			}, this);
			P3.assets.load();
		} else this.loadAssets();
	}

	loadAssets() {
		const files = [
			//game data
			{name: 'languages', url: 'data/' + 'translations' + '.json'},

			//font
			{name: 'luckiest_guy_44', url: 'fonts/' + 'luckiest_guy_44' + '.fnt'},

			//bg
			{name: 'gameplay_background', url: `images/${common.config.theme}/bg/` + 'gameplay_background' + '.jpg'},

			//ui
			{name: 'tutorial_hand', url: 'spine/tutorial/' + 'tutorial_hand' + '.json'},

			//title-logo
			{name: 'game_logo', url: `images/_localisation/logo/` + 'splashart_logo_' + common.config.language + '.png'},

			//audio
			//	--- music
			{name: 'music_winter_magic_loop_00', url: 'audio/music/' + `music_${common.config.theme}_menu_loop_00` + P3.assets.audioFileExt},
			{name: 'music_drawing_loop_00', url: 'audio/music/' + `music_${common.config.theme}_ingame_loop_00` + P3.assets.audioFileExt},
			{name: 'music_level_play_sting', url: 'audio/music/' + `music_${common.config.theme}_play_sting_00` + P3.assets.audioFileExt},
			//	---	sounds
			{name: 'sfx_ambient_desert_loop_00', url: 'audio/sounds/' + 'sfx_ambient_desert_loop_00' + P3.assets.audioFileExt},
			{name: 'sfx_drawer_hide_00', url: 'audio/sounds/' + 'sfx_drawer_hide_00' + P3.assets.audioFileExt},
			{name: 'sfx_drawer_reveal_00', url: 'audio/sounds/' + 'sfx_drawer_reveal_00' + P3.assets.audioFileExt},
			{name: 'sfx_generic_colour_type_change_00', url: 'audio/sounds/' + 'sfx_generic_colour_type_change_00' + P3.assets.audioFileExt},
			{name: 'sfx_tool_eraser_loop_03', url: 'audio/sounds/' + 'sfx_tool_eraser_loop_03' + P3.assets.audioFileExt},
			{name: 'sfx_tool_eraser_select_00', url: 'audio/sounds/' + 'sfx_tool_eraser_select_00' + P3.assets.audioFileExt},
			{name: 'sfx_tool_inkpot_select_00', url: 'audio/sounds/' + 'sfx_tool_inkpot_select_00' + P3.assets.audioFileExt},
			{name: 'sfx_tool_inkpot_use_splat_00', url: 'audio/sounds/' + 'sfx_tool_inkpot_use_splat_00' + P3.assets.audioFileExt},
			{name: 'sfx_tool_magictape_select_00', url: 'audio/sounds/' + 'sfx_tool_magictape_select_00' + P3.assets.audioFileExt},
			{name: 'sfx_tool_magictape_use_end_only_00', url: 'audio/sounds/' + 'sfx_tool_magictape_use_end_only_00' + P3.assets.audioFileExt},
			{name: 'sfx_tool_magictape_use_loop_00', url: 'audio/sounds/' + 'sfx_tool_magictape_use_loop_00' + P3.assets.audioFileExt},
			{name: 'sfx_tool_paintbrush_dry_use_loop_00', url: 'audio/sounds/' + 'sfx_tool_paintbrush_dry_use_loop_00' + P3.assets.audioFileExt},
			{name: 'sfx_tool_paintbrush_select_00', url: 'audio/sounds/' + 'sfx_tool_paintbrush_select_00' + P3.assets.audioFileExt},
			{name: 'sfx_tool_paintbrush_wet_use_loop_00', url: 'audio/sounds/' + 'sfx_tool_paintbrush_wet_use_loop_00' + P3.assets.audioFileExt},
			{name: 'sfx_tool_paintpot_fill_select_01', url: 'audio/sounds/' + 'sfx_tool_paintpot_fill_select_01' + P3.assets.audioFileExt},
			{name: 'sfx_tool_paintpot_fill_use_02', url: 'audio/sounds/' + 'sfx_tool_paintpot_fill_use_02' + P3.assets.audioFileExt},
			{name: 'sfx_tool_pencil_select_00', url: 'audio/sounds/' + 'sfx_tool_pencil_select_00' + P3.assets.audioFileExt},
			{name: 'sfx_tool_pencil_use_loop_00', url: 'audio/sounds/' + 'sfx_tool_pencil_use_loop_00' + P3.assets.audioFileExt},
			{name: 'sfx_tool_spraycan_select_00', url: 'audio/sounds/' + 'sfx_tool_spraycan_select_00' + P3.assets.audioFileExt},
			{name: 'sfx_tool_spraycan_use_loop_00', url: 'audio/sounds/' + 'sfx_tool_spraycan_use_loop_00' + P3.assets.audioFileExt},
			{name: 'sfx_tool_stamp_use_00', url: 'audio/sounds/' + 'sfx_tool_stamp_use_00' + P3.assets.audioFileExt},
			{name: 'sfx_tool_stamps_select_00', url: 'audio/sounds/' + 'sfx_tool_stamps_select_00' + P3.assets.audioFileExt},
			{name: 'sfx_tool_sticker_end_only', url: 'audio/sounds/' + 'sfx_tool_sticker_end_only' + P3.assets.audioFileExt},
			{name: 'sfx_tool_sticker_place_00', url: 'audio/sounds/' + 'sfx_tool_sticker_place_00' + P3.assets.audioFileExt},
			{name: 'sfx_tool_sticker_use_00', url: 'audio/sounds/' + 'sfx_tool_sticker_use_00' + P3.assets.audioFileExt},
			{name: 'sfx_tool_stickers_select_00', url: 'audio/sounds/' + 'sfx_tool_stickers_select_00' + P3.assets.audioFileExt},
			{name: 'sfx_tool_trashcan_confirm_00', url: 'audio/sounds/' + 'sfx_tool_trashcan_confirm_00' + P3.assets.audioFileExt},
			{name: 'sfx_tool_trashcan_select_00', url: 'audio/sounds/' + 'sfx_tool_trashcan_select_00' + P3.assets.audioFileExt},
			{name: 'sfx_tool_undo_step_back_00', url: 'audio/sounds/' + 'sfx_tool_undo_step_back_00' + P3.assets.audioFileExt},
			{name: 'sfx_ui_folder_save_00', url: 'audio/sounds/' + 'sfx_ui_folder_save_00' + P3.assets.audioFileExt},
			{name: 'sfx_ui_newpage_00', url: 'audio/sounds/' + 'sfx_ui_newpage_00' + P3.assets.audioFileExt},
			{name: 'sfx_palette_select_00', url: 'audio/sounds/' + 'sfx_palette_select_00' + P3.assets.audioFileExt},
			{name: 'sfx_palette_close_00', url: 'audio/sounds/' + 'sfx_palette_close_00' + P3.assets.audioFileExt},
			{name: 'sfx_ui_carousel_swipe_00', url: 'audio/sounds/' + 'sfx_ui_carousel_swipe_00' + P3.assets.audioFileExt},
			{name: 'sfx_ui_click_00', url: 'audio/sounds/' + 'sfx_ui_click_00' + P3.assets.audioFileExt},
			{name: 'sfx_ui_rollover_00', url: 'audio/sounds/' + 'sfx_ui_rollover_00' + P3.assets.audioFileExt},
			{name: 'sfx_btn_play_start_doorbell_00', url: 'audio/sounds/' + 'sfx_btn_play_start_doorbell_00' + P3.assets.audioFileExt},

			//particles
			{name: 'eraser_ps', url: 'particles/' + 'particle_emitter_eraser_loop_00' + '.json'},
			{name: 'ink_ps', url: 'particles/' + 'particle_emitter_ink_loop_00' + '.json'},
			{name: 'ink_splatter_ps', url: 'particles/' + 'particle_emitter_inksplatter_loop_01' + '.json'},
			{name: 'brush_ps', url: 'particles/' + 'particle_emitter_paintbrush_loop_00' + '.json'},
			{name: 'pencil_ps', url: 'particles/' + 'particle_emitter_pencil_loop_00' + '.json'},
			{name: 'spray_ps', url: 'particles/' + 'particle_emitter_spray_loop_00' + '.json'},
			{name: 'stamp_ps', url: 'particles/' + 'particle_emitter_stamo_burst_00' + '.json'},
			{name: 'fill_ps', url: 'particles/' + 'particle_paintfill_burst_00' + '.json'}
		];

		/**
		 * Add colour it outline images
		 */
		for (const file of common.levelConfig.outlines.colour_it) {
			files.push(
				{name: file, url: `images/${common.config.theme}/outlines/colour_it/` + file + '.png'}
			);
		}

		/**
		 * Add draw it outline images
		 */
		for (const file of common.levelConfig.outlines.draw_it) {
			files.push(
				{name: file, url: `images/${common.config.theme}/outlines/draw_it/` + file + '.png'}
			);
		}

		if (files.length) {
			this._preloader = new PreloaderScene();
			P3.scene.show(this._preloader);

			P3.assets.addFiles(files, './assets/');
			P3.assets.signals.progress.add(this.onLoadingProgress, this);
			P3.assets.signals.complete.once(this.onLoadingCompleted, this);
			P3.assets.load();
		} else this.start();
	}

	start() {
		this._app = new Application();
		this._app.init();
	}

	onLoadingProgress(event) {
		this._preloader && (this._preloader.loaded = event.progress / 100);
		event.progress % 10 <= 1 && console.log('loading:', event.progress);
	}

	onLoadingCompleted() {
		this._preloader && (this._preloader.loaded = 1);
		if (PIXI.utils.isMobile.any)
			this._preloader.button.tween.start();

		this._preloader.signals.next.once(function() {
			this.start();
		}, this);
	}

	onCanvasResize(correct) {
		if (correct) {
			common.renderer.resize(P3.View.width, P3.View.height);
			if (P3.scene) {
				P3.scene.resize();

				for (let i = 0; i < common.transitions.children.length; ++i) {
					const transition = common.transitions.children[i];
					transition.resize && transition.resize();
				}
			}
		}
	}
}

/**
 * @param copy {string}
 * @param config {object}
 * @param override {string} - overrides default language font type
 * @param jsonName
 * @returns {PIXI.Text|PIXI.BitmapText}
 */
function createText(copy, config, override, jsonName, fontType = null) {
	let textView, conf;
	const str = jsonName || 'languages';
	if (common.config.language === 'ar') {
		conf = {
			font: config.size + ' Noto Kufi Arabic',
			fill: config.fill,
			align: 'center',
			padding: config.padding || 0
		};
		if (config.wordWrapWidth) {
			conf.wordWrap = true;
			conf.wordWrapWidth = config.wordWrapWidth;
		}
		if (config.stroke) {
			conf.stroke = config.stroke;
			conf.strokeThickness = 4 || config.strokeThickness;
		}
		Object.assign(conf, config);
		textView = new PIXI.Text(P3.assets.json(str)[copy][common.config.language], conf);
	} else {
		conf = {
			font: config.size + (fontType || ' Roboto'),
			fill: config.fill,
			align: 'center',
			//letterSpacing: 1,
			padding: config.padding || 0,
			lineJoin: 'round'
		};
		if (config.wordWrapWidth) {
			// conf.wordWrap = true;
			// conf.wordWrapWidth = config.wordWrapWidth;
		}
		if (config.stroke) {
			conf.stroke = config.stroke;
			conf.strokeThickness = 8;
		}
		Object.assign(conf, config);
		textView = new PIXI.Text(P3.assets.json(str)[copy][common.config.language], conf);
	}

	return textView;
}
P3.createText = createText;

/**
 * @param copy {string}
 * @param config
 */
function createMultiText(copy, config) {
	let textView, conf;
	const str = 'languages';
	if (common.config.language === 'ar') {
		conf = {
			font: config.size + ' Noto Kufi Arabic',
			fill: config.fill,
			align: 'center',
			padding: config.padding || 0
		};
		if (config.wordWrapWidth) {
			conf.wordWrap = true;
			conf.wordWrapWidth = config.wordWrapWidth;
		}
		if (config.stroke) {
			conf.stroke = config.stroke;
			conf.strokeThickness = 4 || config.strokeThickness;
		}
		textView = new PIXI.Text(P3.assets.json(str)[copy][common.config.language], conf);
	} else {
		conf = {
			font: config.size + ' Roboto',
			fill: 0xffffff,
			align: 'center',
			letterSpacing: 4,
			padding: config.padding || 0,
			lineJoin: 'round'
		};
		if (config.wordWrapWidth) {
			conf.wordWrap = true;
			conf.wordWrapWidth = config.wordWrapWidth;
		}
		if (config.stroke) {
			conf.stroke = config.stroke;
			conf.strokeThickness = 8;
		}

		//LOL...
		let confCopy = Object.assign({}, conf);
		delete confCopy.fill;
		let realConfig = {//deliberately made larger than needs to be, trying to ovoid overflow
			'default': conf,
			's0': Object.assign({fill: '#fff200'}, confCopy), //
			's1': Object.assign({fill: '#00bff3'}, confCopy), //
			's2': Object.assign({fill: '#00a650'}, confCopy), //
			's3': Object.assign({fill: '#00bff3'}, confCopy), //
			's4': Object.assign({fill: '#ff4b96'}, confCopy), //
			's5': Object.assign({fill: '#ff2600'}, confCopy), //red
			's6': Object.assign({fill: '#f26522'}, confCopy), //orange
			's7': Object.assign({fill: '#fff200'}, confCopy), //yellow
			's8': Object.assign({fill: '#00a650'}, confCopy), //green
			's9': Object.assign({fill: '#fff200'}, confCopy),
			's10': Object.assign({fill: '#00bff3'}, confCopy),
			's11': Object.assign({fill: '#00a650'}, confCopy),
			's12': Object.assign({fill: '#00bff3'}, confCopy),
			's13': Object.assign({fill: '#ff4b96'}, confCopy),
			's14': Object.assign({fill: '#f26522'}, confCopy),
			's15': Object.assign({fill: '#fff200'}, confCopy),
			's16': Object.assign({fill: '#00a650'}, confCopy),
			's17': Object.assign({fill: '#ff2600'}, confCopy),
			's18': Object.assign({fill: '#00a650'}, confCopy),
			's19': Object.assign({fill: '#00a650'}, confCopy),
			's20': Object.assign({fill: '#00a650'}, confCopy),
			's21': Object.assign({fill: '#00a650'}, confCopy),
			's22': Object.assign({fill: '#00a650'}, confCopy),
			's23': Object.assign({fill: '#00a650'}, confCopy)
		};

		let realCopy = P3.assets.json(str)[copy][common.config.language];
		let copyArr = realCopy.split('');
		let finalCopy = '';
		for (let i = 0; i < copyArr.length; i++) {
			finalCopy += '<s' + i + '>';
			finalCopy += copyArr[i];
			finalCopy += '</s' + i + '>';
		}

		//--create the text--//
		textView = new MultiStyleText(finalCopy, realConfig);
	}

	return textView;
}
P3.createMultiText = createMultiText;

function createButton(icon, type) {
	type = type || 'primary_small';
	const buttonStates = new P3.ButtonStates(
		P3.assets.texture('btn_' + type + '_off'),
		P3.assets.texture('btn_' + type + '_over'),
		P3.assets.texture('btn_' + type + '_down'),
		P3.assets.texture('icon_' + icon)
	);

	buttonStates.iconOver = P3.assets.texture('icon_' + icon);
	buttonStates.iconDown = P3.assets.texture('icon_' + icon);

	let button;
	if (icon === 'sound') {
		const states = new P3.ButtonStates();
		states.normal = P3.assets.texture('btn_primary_small_' + 'off');
		states.over = P3.assets.texture('btn_primary_small_' + 'over');
		states.down = P3.assets.texture('btn_primary_small_' + 'down');
		states.icon = P3.assets.texture('icon_sound');
		states.iconAlt = P3.assets.texture('icon_mute');
		button = new P3.MuteButton(states);
	} else
		button = new P3.Button(buttonStates);

	return button;
}

window.createButton = createButton;
